import { useEffect } from 'react';
import type { BrandKit } from '@/lib/api/types';
import { GOOGLE_FONTS } from '@/lib/schemas/brandKit';
import { Input, Select } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { ColorField } from '@/components/forms/ColorField';
import { FileUploader } from '@/components/forms/FileUploader';

interface IdentityTabProps {
  kit: BrandKit;
  onChange: (delta: Partial<BrandKit>) => void;
  onUploadingChange: (uploading: boolean) => void;
}

function loadFont(name: string) {
  const id = `gfont-${name.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name)}:wght@400;600;700&display=swap`;
  document.head.appendChild(link);
}

export function IdentityTab({ kit, onChange, onUploadingChange }: IdentityTabProps) {
  useEffect(() => {
    loadFont(kit.font);
  }, [kit.font]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <FormField label="Campaign name" required>
        <Input
          value={kit.campaignName}
          onChange={(e) => onChange({ campaignName: e.target.value })}
        />
      </FormField>
      <FormField label="Font">
        <Select value={kit.font} onChange={(e) => onChange({ font: e.target.value })}>
          {GOOGLE_FONTS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </Select>
      </FormField>
      <ColorField
        label="Primary color"
        value={kit.primaryColor}
        onChange={(v) => onChange({ primaryColor: v })}
      />
      <ColorField
        label="Secondary color"
        value={kit.secondaryColor}
        onChange={(v) => onChange({ secondaryColor: v })}
      />
      <ColorField
        label="Accent color"
        value={kit.accentColor ?? ''}
        onChange={(v) => onChange({ accentColor: v })}
      />
      <ColorField
        label="Background color"
        value={kit.backgroundColor ?? ''}
        onChange={(v) => onChange({ backgroundColor: v })}
      />
      <ColorField
        label="Font color"
        value={kit.fontColor ?? ''}
        onChange={(v) => onChange({ fontColor: v })}
      />
      <FormField label="Brand logo">
        <FileUploader
          value={kit.brandLogo ?? null}
          onChange={(v) => onChange({ brandLogo: v })}
          onUploadingChange={onUploadingChange}
          preset="logo"
        />
      </FormField>
      <FormField label="Client logo">
        <FileUploader
          value={kit.clientLogo}
          onChange={(v) => onChange({ clientLogo: v ?? null })}
          onUploadingChange={onUploadingChange}
          preset="logo"
        />
      </FormField>
      <FormField label="Background image" className="lg:col-span-2">
        <FileUploader
          value={kit.backgroundImage ?? null}
          onChange={(v) => onChange({ backgroundImage: v })}
          onUploadingChange={onUploadingChange}
          preset="photo"
          editorAspect={16 / 9}
        />
      </FormField>
    </div>
  );
}
